import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import Styles from './LayerStatisticsUtils.module.less';

import React from 'react';
import type { LanguageFile } from '../../helpers/LanguageUtils';
import { truncateNumber } from '../../helpers/NumberUtils';
import type {
  ColorScaleEntry,
  FieldSizeLike,
  LayerStatisticsBucket,
  LayerStatisticsData,
  LayerStatisticsGrid,
  LayerStatisticsItem,
  StatisticsColumn,
} from './sources/LayerStatisticsSourceTypes';

const getTooltip = (
  step: ColorScaleEntry,
  idx: number,
  arr: ColorScaleEntry[],
  continuous: boolean,
  decimals = 2,
  unitString: string | null
) => {
  if (step.label !== undefined && step.label !== null) {
    return <div style={{ display: 'inline-block' }}>{step.label}</div>;
  }

  let tooltip = '';
  let unit = '';
  if (unitString !== undefined && unitString !== null) {
    unit = unitString;
  }

  if ((step.min === null || step.min === undefined) && !step.max) {
    return null;
  }

  let min = Number(step.min).toFixed(decimals);
  let max = Number(step.max).toFixed(decimals);

  if (step.max === Number.MAX_VALUE) {
    return `[${min.toString().replace('.00', '') + unit}[`;
  }

  if (min === max) {
    return min.toString().replace('.00', '') + unit;
  }
  if (idx === 0) {
    if (continuous) {
      tooltip += `< ${max}${unit}`;
    } else {
      tooltip += `[${min}${unit}`;
      if (max) {
        tooltip += ` - ${max}${unit}[`;
      } else {
        tooltip += `]`;
      }
    }
  } else if (idx === arr.length - 1) {
    if (continuous) {
      tooltip += `> ${min}${unit}`;
    } else {
      tooltip += `[${min}${unit} - ${max}${unit}]`;
    }
  } else {
    if (max) {
      tooltip += `[${min}${unit} - ${max}${unit}[`;
    } else {
      tooltip += `[${min}${unit}]`;
    }
  }
  return <div style={{ display: 'inline-block' }}>{tooltip}</div>;
};

export const getScaleItem = (
  step: ColorScaleEntry,
  idx: number,
  arr: ColorScaleEntry[],
  continuous: boolean,
  decimals = 2,
  unitString: string | null
) => {
  const stepTooltip = getTooltip(step, idx, arr, continuous, decimals, unitString);
  const body = (
    <Box
      className={Styles.scale}
      key={idx}
      sx={{
        bgcolor: step.color,
        color: step.contrast,
      }}
    ></Box>
  );
  return (
    <Tooltip key={idx} title={stepTooltip} placement={'bottom'} disableHoverListener={!stepTooltip}>
      {body}
    </Tooltip>
  );
};

const findBucketContainingValue = (
  buckets: LayerStatisticsBucket[],
  value: number
): LayerStatisticsBucket | undefined => {
  return buckets.find(
    (bucket) =>
      bucket.min !== undefined &&
      bucket.max !== undefined &&
      ((bucket.min === bucket.max && value === bucket.min) ||
        (value >= bucket.min && value < bucket.max))
  );
};

const readFiniteGridValue = (grid: LayerStatisticsGrid, y: number, x: number): number | null => {
  const row = grid[y];
  const value = row != null ? row[x] : null;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const countGridValuesInBuckets = (
  buckets: LayerStatisticsBucket[],
  data: LayerStatisticsGrid,
  summaryData: LayerStatisticsGrid | null
): { values: number[]; summaryValues: number[] } => {
  const values: number[] = [];
  const summaryValues: number[] = [];

  data.forEach((row, y) => {
    row.forEach((value, x) => {
      if (value === null) {
        return;
      }

      const bucket = findBucketContainingValue(buckets, value);
      if (bucket === undefined) {
        return;
      }

      values.push(value);
      bucket.count += 1;

      if (summaryData == null) {
        return;
      }

      const summaryValue = readFiniteGridValue(summaryData, y, x);
      if (summaryValue !== null) {
        summaryValues.push(summaryValue);
      }
    });
  });

  return { values, summaryValues };
};

const createStatisticsItems = (
  buckets: LayerStatisticsBucket[],
  bucketedCellCount: number,
  field: FieldSizeLike,
  decimals: number
): LayerStatisticsItem[] =>
  buckets.map((bucket) => {
    const percentage = bucket.count / bucketedCellCount;
    return {
      bucket: bucket,
      item: bucket.item,
      area: truncateNumber(field.size * percentage, decimals),
      percentage: truncateNumber(percentage * 100, decimals),
    };
  });

const summarizeValues = (
  values: number[],
  decimals: number
): Pick<LayerStatisticsData, 'min' | 'mean' | 'max'> => {
  const sortedValues = [...values].sort((a, b) => a - b);
  const sum = sortedValues.reduce((partialSum, a) => partialSum + a, 0);
  return {
    min: truncateNumber(sortedValues[0], decimals),
    mean: truncateNumber(sum / sortedValues.length, decimals),
    max: truncateNumber(sortedValues[sortedValues.length - 1], decimals),
  };
};

export const generateStatisticsData = (
  buckets: LayerStatisticsBucket[],
  data: LayerStatisticsGrid,
  field: FieldSizeLike,
  decimals = 2,
  summaryData: LayerStatisticsGrid | null = null
): LayerStatisticsData | null => {
  const { values, summaryValues } = countGridValuesInBuckets(buckets, data, summaryData);
  const items = createStatisticsItems(buckets, values.length, field, decimals);

  if (values.length === 0 || items.length === 0) {
    return null;
  }

  const summarySource = summaryData ? summaryValues : values;
  return {
    ...summarizeValues(summarySource, decimals),
    items,
  };
};

/**
 * Distributes cells over demand classification buckets by their class index, while
 * min/mean/max summarize the measured value at the same cell in `measuredData`.
 * Cells without a finite measured value still count towards the distribution, so a
 * measured grid that lags behind the classification grid cannot skew percentages.
 */
export const generateStatisticsDataForClassification = (
  buckets: LayerStatisticsBucket[],
  classificationData: LayerStatisticsGrid,
  measuredData: LayerStatisticsGrid,
  field: FieldSizeLike,
  decimals = 2
): LayerStatisticsData | null => {
  let classifiedCellCount = 0;
  const measuredValues: number[] = [];

  classificationData.forEach((row, y) => {
    row.forEach((classIndex, x) => {
      if (classIndex === null) {
        return;
      }

      const bucket = buckets.find(
        (bucket) => bucket.values !== undefined && bucket.values.includes(classIndex)
      );
      if (bucket === undefined) {
        return;
      }

      bucket.count += 1;
      classifiedCellCount += 1;

      const measuredValue = readFiniteGridValue(measuredData, y, x);
      if (measuredValue !== null) {
        measuredValues.push(measuredValue);
      }
    });
  });

  const items = createStatisticsItems(buckets, classifiedCellCount, field, decimals);

  if (measuredValues.length === 0 || items.length === 0) {
    return null;
  }

  return {
    ...summarizeValues(measuredValues, decimals),
    items,
  };
};

export const statisticsOverviewColumns = (LangFile: LanguageFile): StatisticsColumn[] => {
  return [
    {
      dataField: 'min',
      text: LangFile.SurveyStatistics.min,
    },
    {
      dataField: 'avg',
      text: LangFile.SurveyStatistics.mean,
    },
    {
      dataField: 'max',
      text: LangFile.SurveyStatistics.max,
    },
  ];
};

export const statisticsOverviewRows = (values: number[]) => {
  return [
    <TableRow key={'bodyRow'}>
      {values.map((value, index) => {
        return (
          <TableCell className={Styles.cell} key={index} align="left" padding="none">
            {value}
          </TableCell>
        );
      })}
    </TableRow>,
  ];
};

export const statisticsDataColumns = (LangFile: LanguageFile): StatisticsColumn[] => {
  return [
    {
      dataField: 'type',
      text: LangFile.SurveyStatistics.zone,
    },
    {
      dataField: 'area',
      text: 'Ha',
    },
    {
      dataField: 'percentage',
      text: '%',
    },
  ];
};

export const statisticsDataRows = (items: LayerStatisticsItem[]) => {
  return items.map((item, index) => {
    return (
      <TableRow key={'bodyRow' + index}>
        <TableCell className={Styles.cell} key={'type' + index} align="left" padding="none">
          {item.item}
        </TableCell>
        <TableCell className={Styles.cell} key={'area' + index} align="left" padding="none">
          {item.area}
        </TableCell>
        <TableCell className={Styles.cell} key={'percentage' + index} align="left" padding="none">
          {item.percentage}
        </TableCell>
      </TableRow>
    );
  });
};
